import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Plus } from "lucide-react";
import clsx from "clsx";
import {
  ApplyRenameToProjectModal,
  type ApplyRenameToProjectModalProps,
} from "./ApplyRenameToProjectModal";
import { ConfirmActionModal } from "./ConfirmActionModal";
import { ChangePageScaleModal } from "./ChangePageScaleModal";
import { ReviewPageHeaderV3 } from "./ReviewPageHeaderV3";
import { ExportExcelButtonV3 } from "./ExportExcelButtonV3";
import { SmartSurveyorSidebar } from "./SmartSurveyorSidebar";
import {
  HistoryDrawer,
  useHistoryUndo,
  type HistoryUndoRevertedArgs,
} from "./HistoryDrawer";
import {
  FloorPlanElementsTabV3,
  type FloorPlanElementsTabV3Handlers,
} from "./FloorPlanElementsTabV3";
import {
  CableGroupsTabV3,
  type CableGroupsTabV3Handlers,
} from "./CableGroupsTabV3";
import { CableTakeoffTabV3 } from "./CableTakeoffTabV3";
import { CableSourceToolbarOverlay } from "./CableTakeoffTabV3/CableSourceToolbarOverlay";
import type { CablesResult, CablesPublished } from "./utils/fetchCables";
import { useCableTakeoffReview } from "./hooks/useCableTakeoffReview";
import { QuantityGroupsTabV3 } from "./QuantityGroupsTabV3";
import { DiagramViewerV3 } from "./DiagramViewerV3";
import {
  FloorPlanPanelWrapper,
  type ViewerMode,
} from "../ReviewPage/FloorPlanPanelWrapper";
import { skipPanelNavigationZoom } from "../ReviewPage/FloorPlanPanelNew";
import { EmptyStateMessage } from "../ReviewPage/EmptyStateMessage";
import { PlanInteractionHint } from "./PlanInteractionHint";
import { useAutoZoomOnCableView } from "../ReviewPage/hooks/useAutoZoomOnCableView/useAutoZoomOnCableView";
import { buildGroupCircuitPathsState } from "./utils/buildGroupCircuitPathsState";
import { useApprovalStatusV3 } from "./hooks/useApprovalStatusV3";
import { useGroupIconCache } from "./hooks/useGroupIconCache";
import {
  TabsPanel,
  TabsList,
  TabsPanels,
  TabsRoot,
  TabsTrigger,
} from "components/common/Tabs";
import { ExportSelectionModal } from "./ExportSelectionModal";
import { AdditionalUploadModal } from "./AdditionalUploadModal";
import { getPreviewUrlKey } from "../shared/getPreviewUrlKey";
import { useUnfinishedUploadFiles } from "../shared/useUnfinishedUploadFiles";
import { UploadedFilesModal } from "../shared/UploadedFilesModal";
import { useExportFloorPlanPdf } from "./hooks/useExportFloorPlanPdf";
import { useFloorPlanLayout } from "./hooks/useFloorPlanLayout";
import { useLayoutDetection } from "./hooks/useLayoutDetection";
import { useCableRoomsOverlay } from "./hooks/useCableRoomsOverlay";
import { useGroupSelectionV3 } from "./hooks/useGroupSelectionV3/useGroupSelectionV3";
import { useInteractionMode } from "./hooks/useInteractionMode";
import { useSelectionAreasMode } from "./hooks/useSelectionAreasMode";
import { useRoomsEditor } from "./hooks/useRoomsEditor";
import { usePlanExportActions } from "./hooks/usePlanExportActions";
import { useSelectedRoom } from "./hooks/useSelectedRoom";
import { useSelectedZone } from "./hooks/useSelectedZone";
import { ACTIVE_PAGE_INDEX } from "./hooks/useSelectedRoom/constants";
import { useFloorPlanLayoutView } from "./hooks/useFloorPlanLayoutView";
import { LayoutViewTabs } from "./LayoutViewTabs";
import { LayoutDetectionStrip } from "./LayoutDetectionStrip";
import { useRoomScopedPlanElements } from "./hooks/useRoomScopedPlanElements";
import { WorkModeTabs } from "./WorkModeTabs";
import { BetaBadge } from "./BetaBadge";
import { useMapInteractionAdapter } from "./hooks/useMapInteractionAdapter";
import { useTraversingState } from "./hooks/useTraversingState";
import { useRouteGroupSelectionV3 } from "./hooks/useRouteGroupSelectionV3/useRouteGroupSelectionV3";
import { usePageScaleRecalibration } from "./hooks/usePageScaleRecalibration";
import { useFloorPlanVisibility } from "./hooks/useFloorPlanVisibility";
import { useBoardSelectionV3 } from "./hooks/useBoardSelectionV3";
import { useBulkApprovalModal } from "./hooks/useBulkApprovalModal";
import { useGridReview } from "./hooks/useGridReview";
import { useMarkerNotesVisibility } from "./hooks/useMarkerNotesVisibility";
import { GridReviewModal } from "./GridReviewModal";
import { useTabSearchV3 } from "./hooks/useTabSearchV3";
import { useTabSortV3 } from "./hooks/useTabSortV3";
import { useReviewPageOnboarding } from "./hooks/useReviewPageOnboarding";
import { OnboardingTour } from "./Onboarding/OnboardingTour";
import { CablesChatPanel } from "./CableTakeoffTabV3/CablesChatPanel";
import { ONBOARDING_ANCHORS } from "./Onboarding/onboardingSelectors";
import { pickBestTab } from "./utils/pickBestTab";
import type { FloorPlanPanelHandlers } from "../ReviewPage/FloorPlanPanelNew/types";
import type {
  BOQExtractionData,
  BoardDiagramV3,
  DiagramRecord,
  FloorPlanFolderImportResult,
  FloorPlanFolderRecord,
  FloorPlanLayoutView,
  PlanV3,
  RightTab,
  SidebarMode,
  WorkMode,
} from "./utils/types";
import type { FloorPlanRecord } from "../SmartSurveyorForRudolfFritz/hooks/types";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { ANALYTICS_TAB_BY_RIGHT_TAB } from "./constants";
import { findElementGroupLabel } from "./utils/findElementGroupLabel";
import {
  PageCalibrationModal,
  usePageCalibration,
} from "./PageCalibrationModal";
import { usePageRotation } from "./hooks/usePageRotation";
import type {
  UpdateFloorPlanElement,
  DeleteFloorPlanElement,
  DeleteFloorPlanElementGroup,
  BulkCreateFloorPlanElements,
  BulkDeleteFloorPlanElements,
  BulkMoveFloorPlanElements,
  BulkCreateFloorPlanRoutes,
  AddFloorPlanSelectionArea,
  UpdateFloorPlanSelectionArea,
  DeleteFloorPlanSelectionArea,
  RenamePlan,
  RenameGroups,
  DeleteRoute,
  DeleteRouteGroup,
  RenameRouteGroups,
  UpdateRoute,
  UpdateRouteGeometry,
  SetFloorPlanPageCalibration,
  SetFloorPlanPageRotation,
  SetFloorPlanElementGroupLvMatch,
  SetFloorPlanGroupsLvMatch,
  SetFloorPlanRouteGroupLvMatch,
  SetBoardItemLvMatch,
  DeleteBoardItemGroup,
  MarkElementTypesReviewed,
  MarkRouteTypesReviewed,
  SetElementTypeNameStatus,
  SetCableTypeNameStatus,
  ApplyHistoryChanges,
} from "./types";

const EMPTY_ARRAY: never[] = [];

// Lazy so the overview grid (and @tanstack/react-table) load only when opened
const OverviewModal = lazy(() =>
  import("./OverviewModal").then((module) => ({
    default: module.OverviewModal,
  })),
);

interface ReviewPageV3Props {
  onBack: () => void;
  projectId: string;
  projectName?: string;
  boqExtraction?: BOQExtractionData | null;
  cables?: CablesResult | null;
  cablesPublished?: CablesPublished | null;
  hasResearchReport?: boolean;
  onViewResearchReport: () => void;

  // Plan loader results
  planRecords: FloorPlanRecord[];
  selectedPlanRecordIndex: number | null;
  pendingPlanRecordIndex: number | null;
  selectedPlan: PlanV3 | null;
  selectPlanRecord: (recordIndex: number) => void;
  clearPlanSelection: () => void;
  updatePlanRecordName: (recordIndex: number, newName: string) => void;
  updatePlanRecordArchivedAt: (
    recordIndex: number,
    archivedAt: string | null,
  ) => void;
  removePlanRecord: (recordIndex: number) => void;
  updatePlanRecordFolderId: (
    floorPlanId: number,
    folderId: number | null,
  ) => void;

  // Folder records
  folderRecords: FloorPlanFolderRecord[];
  upsertFolderRecords: (folders: FloorPlanFolderRecord[]) => void;
  renameFolderRecord: (folderId: number, name: string) => void;
  removeFolderRecord: (folderId: number) => void;
  applyFolderImport: (result: FloorPlanFolderImportResult) => void;

  // Plan mutations
  updateFloorPlanElement: UpdateFloorPlanElement;
  deleteFloorPlanElement: DeleteFloorPlanElement;
  deleteFloorPlanElementGroup: DeleteFloorPlanElementGroup;
  bulkCreateFloorPlanElements: BulkCreateFloorPlanElements;
  bulkDeleteFloorPlanElements: BulkDeleteFloorPlanElements;
  bulkMoveFloorPlanElements: BulkMoveFloorPlanElements;
  applyHistoryChanges: ApplyHistoryChanges;
  invalidateHistory: () => void;
  bulkCreateFloorPlanRoutes: BulkCreateFloorPlanRoutes;
  addFloorPlanSelectionArea: AddFloorPlanSelectionArea;
  updateFloorPlanSelectionArea: UpdateFloorPlanSelectionArea;
  deleteFloorPlanSelectionArea: DeleteFloorPlanSelectionArea;
  setFloorPlanPageCalibration: SetFloorPlanPageCalibration;
  setFloorPlanPageRotation: SetFloorPlanPageRotation;
  renameGroups: RenameGroups;
  renamePlan: RenamePlan;
  deleteRoute: DeleteRoute;
  deleteRouteGroup: DeleteRouteGroup;
  renameRouteGroups: RenameRouteGroups;
  markElementTypesReviewed: MarkElementTypesReviewed;
  markRouteTypesReviewed: MarkRouteTypesReviewed;
  setElementTypeNameStatus: SetElementTypeNameStatus;
  setCableTypeNameStatus: SetCableTypeNameStatus;
  updateRoute: UpdateRoute;
  updateRouteGeometry: UpdateRouteGeometry;

  // Diagram loader results
  diagramRecords: DiagramRecord[];
  selectedDiagramRecordIndex: number;
  pendingDiagramRecordIndex: number | null;
  selectedDiagram: BoardDiagramV3 | null;
  selectDiagramRecord: (recordIndex: number) => void;
  /** Every schematic fetched so far, so Karl can search all of them. */
  loadedDiagrams: Record<number, BoardDiagramV3>;
  loadAllDiagrams: () => void;

  // LV match mutations
  setFloorPlanElementGroupLvMatch: SetFloorPlanElementGroupLvMatch;
  setFloorPlanGroupsLvMatch: SetFloorPlanGroupsLvMatch;
  setFloorPlanRouteGroupLvMatch: SetFloorPlanRouteGroupLvMatch;
  setBoardItemLvMatch: SetBoardItemLvMatch;
  deleteBoardItemGroup: DeleteBoardItemGroup;

  canEdit?: boolean;
  canDelete?: boolean;
  isSuperAdmin?: boolean;
  canAccessCableTakeoffBeta?: boolean;
  canManagePrices?: boolean;
}

export const ReviewPageV3 = ({
  onBack,
  projectId,
  projectName,
  boqExtraction,
  cables,
  cablesPublished,
  hasResearchReport,
  onViewResearchReport,
  planRecords,
  selectedPlanRecordIndex,
  pendingPlanRecordIndex,
  selectedPlan,
  selectPlanRecord,
  clearPlanSelection,
  updatePlanRecordName,
  updatePlanRecordArchivedAt,
  removePlanRecord,
  updatePlanRecordFolderId,
  folderRecords,
  upsertFolderRecords,
  renameFolderRecord,
  removeFolderRecord,
  applyFolderImport,
  updateFloorPlanElement,
  deleteFloorPlanElement,
  deleteFloorPlanElementGroup,
  bulkCreateFloorPlanElements,
  bulkDeleteFloorPlanElements,
  bulkMoveFloorPlanElements,
  applyHistoryChanges,
  invalidateHistory,
  bulkCreateFloorPlanRoutes,
  addFloorPlanSelectionArea,
  updateFloorPlanSelectionArea,
  deleteFloorPlanSelectionArea,
  setFloorPlanPageCalibration,
  setFloorPlanPageRotation,
  renameGroups,
  renamePlan,
  deleteRoute,
  deleteRouteGroup,
  renameRouteGroups,
  markElementTypesReviewed,
  markRouteTypesReviewed,
  setElementTypeNameStatus,
  setCableTypeNameStatus,
  updateRoute,
  updateRouteGeometry,
  diagramRecords,
  selectedDiagramRecordIndex,
  pendingDiagramRecordIndex,
  selectedDiagram,
  selectDiagramRecord,
  loadedDiagrams,
  loadAllDiagrams,
  setFloorPlanElementGroupLvMatch,
  setFloorPlanGroupsLvMatch,
  setFloorPlanRouteGroupLvMatch,
  setBoardItemLvMatch,
  deleteBoardItemGroup,
  canEdit = true,
  canDelete = false,
  isSuperAdmin = false,
  canAccessCableTakeoffBeta = false,
  canManagePrices = false,
}: ReviewPageV3Props) => {
  const { t, i18n } = useTranslation();

  const interaction = useInteractionMode();
  const { interactionMode, resetToIdle: resetInteraction } = interaction;

  const traversing = useTraversingState({
    subscribeOnReset: interaction.subscribeOnReset,
  });
  const { isTraversing, stopTraversing } = traversing;

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const toggleHistoryDrawer = useCallback(() => {
    if (!isHistoryOpen) {
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_HISTORY_OPENED);
    }
    setIsHistoryOpen((current) => !current);
  }, [isHistoryOpen]);
  const closeHistoryDrawer = useCallback(() => setIsHistoryOpen(false), []);
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);
  const openOverviewModal = useCallback(() => {
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_OVERVIEW_OPENED);
    setIsOverviewOpen(true);
  }, []);
  const closeOverviewModal = useCallback(() => setIsOverviewOpen(false), []);

  const [sidebarMode, setSidebarMode] = useState<SidebarMode>("plans");
  const [isPlanRailCollapsed, setIsPlanRailCollapsed] = useState(false);
  const [rightTab, setRightTab] = useState<RightTab>(() =>
    pickBestTab(selectedPlan, false),
  );
  const [viewerMode, setViewerMode] = useState<ViewerMode>("image");
  const [areAreasVisible, setAreAreasVisible] = useState(true);
  const toggleAreasVisibility = useCallback(
    () => setAreAreasVisible((current) => !current),
    [],
  );

  const [workMode, setWorkMode] = useState<WorkMode>("planning");
  const isExecutionMode = workMode === "execution";

  const {
    layout,
    isLoading: isLayoutLoading,
    isError: isLayoutError,
  } = useFloorPlanLayout({
    floorPlanId: selectedPlan?.floorPlanId,
    isEnabled: isExecutionMode,
  });
  const rooms = layout.rooms;

  // Followed from every execution view, so a finished detection lands whichever one is open.
  const layoutDetection = useLayoutDetection({
    floorPlanId: selectedPlan?.floorPlanId,
    isEnabled: isExecutionMode,
  });

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const openExportModal = useCallback(() => setIsExportModalOpen(true), []);
  const closeExportModal = useCallback(() => setIsExportModalOpen(false), []);

  const [isAdditionalUploadOpen, setIsAdditionalUploadOpen] = useState(false);

  const openAdditionalUploadModal = useCallback(() => {
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ADDITIONAL_UPLOAD_OPENED);
    setIsAdditionalUploadOpen(true);
  }, []);
  const closeAdditionalUploadModal = useCallback(
    () => setIsAdditionalUploadOpen(false),
    [],
  );

  const [isUploadedFilesModalOpen, setIsUploadedFilesModalOpen] =
    useState(false);

  const openUploadedFilesModal = useCallback(
    () => setIsUploadedFilesModalOpen(true),
    [],
  );

  const closeUploadedFilesModal = useCallback(
    () => setIsUploadedFilesModalOpen(false),
    [],
  );

  const {
    files: unfinishedUploadFiles,
    refetch: refetchUnfinishedUploadFiles,
  } = useUnfinishedUploadFiles({ projectId });

  const handleAdditionalUploadComplete = useCallback(() => {
    refetchUnfinishedUploadFiles();
    setIsAdditionalUploadOpen(false);
  }, [refetchUnfinishedUploadFiles]);

  const isDiagramsMode = sidebarMode === "diagrams";
  const isCablesView = !isDiagramsMode && rightTab === "cables";

  const boqPositions = boqExtraction?.positions;

  const floorPlanPanelHandlers = useRef<FloorPlanPanelHandlers>(null);

  const {
    elementSearch,
    setElementSearch,
    clearElementSearch,
    cableSearch,
    setCableSearch,
    boardSearch,
    setBoardSearch,
  } = useTabSearchV3();

  // The entered room and zone outlive every interaction mode; only picking a room on the plan needs idle.
  const isLayoutPanelShown = isExecutionMode && rightTab === "plan";

  const isRoomSelectionEnabled =
    isLayoutPanelShown &&
    (interaction.interactionMode.kind === "idle" ||
      interaction.interactionMode.kind === "merging-rooms");

  const roomSelection = useSelectedRoom({
    rooms,
    isEnabled: isLayoutPanelShown,
    resetKey: selectedPlan?.floorPlanId,
    floorPlanPanelHandlers,
    onRoomChange: clearElementSearch,
  });

  const selectedZone = useSelectedZone({
    layout,
    isEnabled: isLayoutPanelShown,
    resetKey: selectedPlan?.floorPlanId,
    floorPlanPanelHandlers,
    onEnterRoom: roomSelection.selectRoom,
  });

  // Unlike the rooms level this needs no rooms, so a page with walls alone still draws them.
  const isLayoutShown =
    isRoomSelectionEnabled && roomSelection.selectedRoom === null;

  const { clearRoomSelection } = roomSelection;

  const roomScopedPlanElements = useRoomScopedPlanElements({
    selectedPlan,
    rooms: roomSelection.pageRooms,
  });

  const { notesCount, areNotesVisible, toggleNotesVisibility } =
    useMarkerNotesVisibility({ byGroupId: roomScopedPlanElements.byGroupId });

  const elementsTabHandlers = useRef<FloorPlanElementsTabV3Handlers>(null);
  const cablesTabHandlers = useRef<CableGroupsTabV3Handlers>(null);

  const selectedFloorPlanId = selectedPlan?.floorPlanId;
  const prevFloorPlanIdRef = useRef(selectedFloorPlanId);

  const planElementTypeIds = useMemo(
    () =>
      Object.values(selectedPlan?.groupMeta ?? {}).map(
        (meta) => meta.element_type_id,
      ),
    [selectedPlan?.groupMeta],
  );

  const { iconByGroupId } = useGroupIconCache({ plan: selectedPlan });

  const routeGroups = selectedPlan?.routeGroups ?? EMPTY_ARRAY;

  const planRouteTypeIds = useMemo(
    () => routeGroups.map((group) => group.route_type_id),
    [routeGroups],
  );

  const selectedDiagramItemTypeIds = useMemo(
    () =>
      selectedDiagram?.itemTypes.map(
        (itemType) => itemType.diagram_item_type_id,
      ) ?? [],
    [selectedDiagram],
  );

  const {
    isLoadingApprovals,
    allPlanElementsApproved,
    allPlanCablesApproved,
    allDiagramItemsApproved,
    toggleFloorPlanGroupApproval,
    toggleCableGroupApproval,
    toggleQuantityGroupApproval,
    bulkApproveElementTypes,
    bulkApproveRouteTypes,
    bulkApproveQuantityTypes,
    isFloorPlanGroupApproved,
    isCableGroupApproved,
    isQuantityGroupApproved,
    applyApprovalChanges,
  } = useApprovalStatusV3({
    projectId,
    planElementTypeIds,
    planRouteTypeIds,
    selectedDiagramItemTypeIds,
    floorPlanId: selectedFloorPlanId,
    markElementTypesReviewed,
    markRouteTypesReviewed,
    canEdit,
    stopTraversing,
    onApprovalsChanged: invalidateHistory,
  });

  const {
    selectedItemType,
    selectedOccurrenceIndex,
    selectedOccurrence,
    occurrenceCount,
    canGoPrev: canGoPrevOccurrence,
    canGoNext: canGoNextOccurrence,
    handleSelectItemType,
    handleSelectOccurrence,
    goNextOccurrence,
    goPrevOccurrence,
    goNextUnseen: goNextUnseenOccurrence,
    selectFirstItemType,
    handleStartTraversing: handleStartBoardTraversing,
    seenIds: boardSeenIds,
    handleSaveLvMatch: handleSaveBoardItemLvMatch,
    handleDeleteItemGroup,
  } = useBoardSelectionV3({
    canEdit,
    diagram: selectedDiagram,
    isQuantityGroupApproved,
    toggleQuantityGroupApproval,
    onSetBoardItemLvMatch: setBoardItemLvMatch,
    onDeleteItemGroup: deleteBoardItemGroup,
    traversing,
  });

  const {
    totalCableMeters,
    routeGroupLabels,
    byGroupLabel,
    isAllRoutesMode,
    selectedGroupLabel: selectedRouteGroupLabel,
    selectedGroupRoutes,
    selectedRoute,
    handleSelectAllRoutes,
    handleSelectGroup: handleSelectRouteGroup,
    handleJumpToSelectedRouteGroup,
    handleSelectRoute,
    selectPlanRoute,
    renameRouteGroupsFromOverview,
    handleDeleteRoute,
    handleDeleteRouteGroup,
    handleRenameRouteGroup,
    pendingRouteRename,
    routeRenameSubmitAction,
    confirmRouteRenameForAllPlans,
    confirmRouteRenameForCurrentPlan,
    cancelPendingRouteRename,
    handleMarkReviewed: handleMarkRouteGroupReviewed,
    handleSetCableTypeStatus,
    handleSaveLvMatch: handleSaveRouteGroupLvMatch,
    handleChangeRouteType,
    handleStartTraversing: handleStartRouteTraversing,
    seenIds: routeSeenIds,
    isRouteCreationMode,
    openRouteCreationMode,
    routeCreationModeState,
    isRouteDrawingActive,
    routeCreationOverlay,
    handleRouteMapClick,
    handleRouteMapMouseMove,
    isPickingCalibration,
    calibrationState,
    startCalibrating,
    startRecalibrating,
    setCalibrationPoint,
    stopCalibrating,
    isPathEditing,
    pathEditingOverlay,
    isPathEditingDirty,
    isPathEditingSubmitting,
    pathEditingMeters,
    startPathEditing,
    submitPathEditing,
    cancelPathEditing,
    handlePathEditingMapMouseMove,
    followHistoryChanges: followRouteHistoryChanges,
  } = useRouteGroupSelectionV3({
    canEdit,
    selectedPlan,
    routeGroups,
    floorPlanId: selectedPlan?.floorPlanId,
    onDeleteRoute: deleteRoute,
    onDeleteRouteGroup: deleteRouteGroup,
    onRenameRouteGroups: renameRouteGroups,
    onMarkRouteTypeReviewed: markRouteTypesReviewed,
    onSetCableTypeNameStatus: setCableTypeNameStatus,
    onSetRouteGroupLvMatch: setFloorPlanRouteGroupLvMatch,
    onUpdateRoute: updateRoute,
    onUpdateRouteGeometry: updateRouteGeometry,
    onBulkCreateRoutes: bulkCreateFloorPlanRoutes,
    floorPlanPanelHandlers,
    isCableGroupApproved,
    toggleCableGroupApproval,
    interaction,
    traversing,
  });

  const {
    groupIds,
    byGroupId,
    totalElementCount,
    isAllElementsMode,
    selectedGroupLabel,
    selectedGroupElements,
    selectedElement,
    draftSelectedElement,
    handleSelectAllElements,
    handleSelectGroup,
    selectNextGroupAfter,
    followHistoryChanges: followElementHistoryChanges,
    handleJumpToSelectedElementGroup,
    handleSelectElement,
    handleDeleteElement,
    handleDeleteGroup,
    handleRenameGroup,
    renameGroupsFromOverview,
    pendingRename,
    renameSubmitAction,
    confirmRenameForAllPlans,
    confirmRenameForCurrentPlan,
    cancelPendingRename,
    handleMarkReviewed: handleMarkElementGroupReviewed,
    handleSetNameStatus,
    resetPlanSelection,
    selectedElementForm,
    isEditingElement,
    toggleEditMode,
    handlePickPositionFromMap,
    handleChangeSelectedBbox,
    isCreationMode,
    openCreationMode,
    creationModeState,
    creationPreviewElements,
    creationActivePreviewElement,
    creationModifier,
    isMultiAddModeEnabled,
    toggleMultiAdd,
    selectPlanElement,
    handleStartTraversing,
    seenIds,
    handleSaveLvMatch: handleSaveElementGroupLvMatch,
  } = useGroupSelectionV3({
    canEdit,
    selectedPlan,
    // While rooms are being drawn the whole plan stays visible, so the drawer sees what they enclose.
    rooms: interaction.isDrawingRooms ? EMPTY_ARRAY : roomSelection.scopeRooms,
    selectedRoom: roomSelection.selectedRoom,
    onUpdateElement: updateFloorPlanElement,
    onDeleteElement: deleteFloorPlanElement,
    onDeleteGroup: deleteFloorPlanElementGroup,
    onBulkCreateElements: bulkCreateFloorPlanElements,
    onRenameGroups: renameGroups,
    onMarkElementTypeReviewed: markElementTypesReviewed,
    onSetElementTypeNameStatus: setElementTypeNameStatus,
    onSetElementGroupLvMatch: setFloorPlanElementGroupLvMatch,
    floorPlanPanelHandlers,
    isFloorPlanGroupApproved,
    toggleFloorPlanGroupApproval,
    interaction,
    traversing,
  });

  const { isGridReviewOpen, openGridReview, closeGridReview } = useGridReview();

  const handleGridReviewJump = useCallback(
    (elementId: string) => {
      closeGridReview();
      selectPlanElement(elementId);
    },
    [closeGridReview, selectPlanElement],
  );

  const handleGridReviewScopeEmptied = useCallback(() => {
    closeGridReview();
    if (selectedGroupLabel) {
      selectNextGroupAfter(selectedGroupLabel);
    }
  }, [closeGridReview, selectedGroupLabel, selectNextGroupAfter]);

  const renameModalProps: ApplyRenameToProjectModalProps | null = pendingRename
    ? {
        isOpen: true,
        oldGroupLabel: pendingRename.oldGroupLabel,
        newGroupLabel: pendingRename.newGroupLabel,
        plans: pendingRename.plans,
        submittingAction: renameSubmitAction,
        onApplyToAll: confirmRenameForAllPlans,
        onApplyToCurrent: confirmRenameForCurrentPlan,
        onCancel: cancelPendingRename,
      }
    : pendingRouteRename
      ? {
          isOpen: true,
          oldGroupLabel: pendingRouteRename.oldGroupLabel,
          newGroupLabel: pendingRouteRename.newGroupLabel,
          plans: pendingRouteRename.plans,
          submittingAction: routeRenameSubmitAction,
          onApplyToAll: confirmRouteRenameForAllPlans,
          onApplyToCurrent: confirmRouteRenameForCurrentPlan,
          onCancel: cancelPendingRouteRename,
        }
      : null;

  const calibration = usePageCalibration({
    selectedPlan,
    calibrationState,
    isPickingCalibration,
    setCalibrationPoint,
    stopCalibrating,
    routeCreationOverlay,
    onSuccess: setFloorPlanPageCalibration,
  });

  const pageScaleRecalibration = usePageScaleRecalibration({
    selectedPlan,
    interactionMode,
    startRecalibrating,
    onSuccess: setFloorPlanPageCalibration,
  });

  const { rotation: pageRotation, rotateClockwise } = usePageRotation({
    selectedPlan,
    pageIndex: 0,
    onSuccess: setFloorPlanPageRotation,
  });

  const visibilityResetKey = useMemo(
    () =>
      [
        selectedPlanRecordIndex,
        sidebarMode,
        rightTab,
        selectedGroupLabel ?? "no-element-group",
        selectedRouteGroupLabel ?? "no-route-group",
        roomSelection.selectedRoom?.id ?? "no-room",
        isCreationMode ? "creating-elements" : "idle-elements",
        isRouteCreationMode ? "creating-routes" : "idle-routes",
      ].join("-"),
    [
      selectedPlanRecordIndex,
      sidebarMode,
      rightTab,
      selectedGroupLabel,
      selectedRouteGroupLabel,
      roomSelection.selectedRoom?.id,
      isCreationMode,
      isRouteCreationMode,
    ],
  );

  const {
    hiddenElementIds,
    hiddenRouteIds,
    toggleElementsVisibility,
    toggleRoutesVisibility,
    areAllElementsHidden,
    areAllRoutesHidden,
  } = useFloorPlanVisibility({ resetKey: visibilityResetKey });

  const selectionAreas = useSelectionAreasMode({
    selectedPlan,
    interactionMode,
    isSelectionAreasMode: interaction.isSelectionAreasMode,
    areAreasVisible,
    startSelectionAreas: interaction.startSelectionAreas,
    setSelectionAreasStep: interaction.setSelectionAreasStep,
    stopSelectionAreas: interaction.stopSelectionAreas,
    addFloorPlanSelectionArea,
    updateFloorPlanSelectionArea,
    deleteFloorPlanSelectionArea,
  });

  const roomsEditor = useRoomsEditor({
    selectedPlan,
    rooms,
    interactionMode,
    startDrawingRooms: interaction.startDrawingRooms,
    setRoomsStep: interaction.setRoomsStep,
    startEditingRoom: interaction.startEditingRoom,
    stopRooms: interaction.stopRooms,
    startMergingRooms: interaction.startMergingRooms,
    stopMergingRooms: interaction.stopMergingRooms,
    mergeableRoomIds:
      selectedZone.selectedZone === null
        ? null
        : selectedZone.selectedZoneRoomIds,
    floorPlanPanelHandlers,
  });

  const { requestClose: requestRoomsEditorClose } = roomsEditor;

  // A detection replaces the rooms when it lands, so none starts over rooms being edited.
  const isEditingRooms =
    roomsEditor.creation !== null ||
    roomsEditor.inspector !== null ||
    roomsEditor.merge.selectedRoomIds !== null;

  const { clearZoneSelection } = selectedZone;

  // A view tab leaves the entered room or zone and the rooms editor, so the whole view is shown.
  const leaveRoomsForView = useCallback(() => {
    clearRoomSelection();
    clearZoneSelection();
    requestRoomsEditorClose();
  }, [clearRoomSelection, clearZoneSelection, requestRoomsEditorClose]);

  const floorPlanLayoutView = useFloorPlanLayoutView({
    layout,
    metersPerPdfPoint:
      selectedPlan?.metersPerPdfPointByPageIndex.get(ACTIVE_PAGE_INDEX) ?? 0,
    selectedZoneId: selectedZone.selectedZone?.id ?? null,
    mergeRoomIds: roomsEditor.merge.selectedRoomIds,
    onViewSelected: leaveRoomsForView,
  });

  const layoutPlanClickByView = {
    zones: selectedZone.handlePlanClick,
    rooms: roomSelection.handleRoomPickClick,
    walls: undefined,
  };

  const handleLayoutPlanClick =
    layoutPlanClickByView[floorPlanLayoutView.state.view];

  const mapInteraction = useMapInteractionAdapter({
    interactionMode,
    creationModifier,
    isRouteDrawingActive,
    isPickingCalibration,
    routeCreationOverlay,
    pathEditingOverlay,
    calibrationOverlay: calibration.overlay,
    onPickPositionFromMap: handlePickPositionFromMap,
    onRouteMapClick: handleRouteMapClick,
    onRouteMapMouseMove: handleRouteMapMouseMove,
    onPathEditingMapMouseMove: handlePathEditingMapMouseMove,
    onSelectionAreasMapClick: selectionAreas.handleMapClick,
    onSelectionAreasMapMouseMove: selectionAreas.handleMapMouseMove,
    onRoomsMapClick: roomsEditor.handleMapClick,
    onRoomsMapMouseMove: roomsEditor.handleMapMouseMove,
    onCalibrationMapClick: calibration.handleMapClick,
    onCalibrationMapMouseMove: calibration.handleMapMouseMove,
  });

  const { planExportActions } = usePlanExportActions({
    selectedPlan,
    isCablesView,
    isExecutionMode,
    routeGroups,
    groupIds: roomScopedPlanElements.groupIds,
    byGroupId: roomScopedPlanElements.byGroupId,
    rooms: roomsEditor.rooms,
    boqPositions,
    onExportPdf: openExportModal,
  });

  const {
    modalProps,
    openForElementTypes,
    openForCableGroups,
    openForQuantityGroups,
  } = useBulkApprovalModal({
    planElementTypeIds,
    planRouteTypeIds,
    selectedDiagramItemTypeIds,
    allPlanElementsApproved,
    allPlanCablesApproved,
    allDiagramItemsApproved,
    bulkApproveElementTypes,
    bulkApproveRouteTypes,
    bulkApproveQuantityTypes,
  });

  const {
    elementSort,
    setElementSort,
    cableSort,
    setCableSort,
    boardSort,
    setBoardSort,
  } = useTabSortV3();

  const routeGroupOptions = useMemo(
    () =>
      routeGroupLabels.map((label) => ({
        value: label,
        label,
      })),
    [routeGroupLabels],
  );

  const imageScaleFactor = useMemo(
    () => selectedPlan?.pageScaleFactors?.get(0) ?? 1,
    [selectedPlan?.pageScaleFactors],
  );

  const {
    cableTakeoff,
    canSeeCableTakeoff,
    isCableTakeoffView,
    karlRouteStatus,
    karlRouteUnderReview,
    karlBoardUnderReview,
    setKarlBoardUnderReview,
    listCircuitBoard,
    setListCircuitBoard,
    setCableTypeCircuits,
    routeUnderReviewOnPlan,
    karlAwaitingRunPick,
    registerKarlReset,
    resetKarlThread,
    registerKarlNewChat,
    startNewKarlChat,
    registerKarlAskAbout,
    askKarlAboutRoute,
    selectCableCircuitFromList,
    circuitKeys,
    circuitBreakers,
    unfilledWays,
    feedPointOptions,
    cableTypeOptions,
    karlPlanControl,
    karlDiagramControl,
    karlSourceEdit,
    karlWalls,
    karlLiveTakeoff,
    karlOpeningFacts,
  } = useCableTakeoffReview({
    projectId,
    language: i18n.language,
    cables,
    cablesPublished,
    selectedPlan,
    rightTab,
    imageScaleFactor,
    pageScale: pageScaleRecalibration.pageScale,
    byGroupId,
    iconByGroupId,
    canAccessCableTakeoffBeta,
    floorPlanPanelHandlers,
    selectedDiagram,
    diagramRecords,
    loadedDiagrams,
    loadAllDiagrams,
    selectedDiagramRecordIndex,
    selectDiagramRecord,
    selectItemType: handleSelectItemType,
    selectOccurrence: handleSelectOccurrence,
    setSidebarMode,
    setRightTab,
  });

  useEffect(() => {
    setIsPlanRailCollapsed(isCableTakeoffView);
    if (isCableTakeoffView) {
      setIsHistoryOpen(false);
    }
  }, [isCableTakeoffView]);
  // Karl owns the third column on the cable tab; this also covers the render before the effect closes it.
  const isHistoryShown = isHistoryOpen && !isCableTakeoffView;

  /**
   * The takeoff owns plan clicks in its own tab — but only while nothing else
   * has claimed them.
   *
   * Setting the page scale is TWO CLICKS ON THE PLAN, and so is drawing a route
   * or an area. The takeoff was taking every click the moment its tab was open,
   * so the calibration armed, the crosshair appeared, and neither point ever
   * landed. Any interaction mode that is running has asked for the plan first.
   */
  const doesTakeoffOwnPlanClicks =
    isCableTakeoffView && interactionMode.kind === "idle";

  // The board's own LABEL on the box, which is what the marker beside it says.
  /**
   * His verdict on whether a drawn type carries cable. Held by the ROUTING
   * module against the route type, so it survives a re-run of the estimation
   * without the plan having to carry a routing concept. The stored figures go
   * stale by themselves: the tray fingerprint is taken over the accepted trays,
   * so admitting one changes it.
   */
  const handleSetCarriesCable = useCallback(
    async (routeTypeId: number, carriesCable: boolean | null) => {
      await cableTakeoff.trayVerdicts.setVerdict(routeTypeId, carriesCable);
      invalidateHistory();
    },
    [cableTakeoff.trayVerdicts, invalidateHistory],
  );

  const cableRooms = useCableRoomsOverlay({
    floorPlanId: selectedPlan?.floorPlanId,
    metersPerPdfPoint:
      selectedPlan?.metersPerPdfPointByPageIndex.get(ACTIVE_PAGE_INDEX) ?? 0,
    isEnabled: isCableTakeoffView,
  });
  // The slot below Karl that an opened run renders into; Karl's steps keep the whole column.
  const [cableDetailHost, setCableDetailHost] = useState<HTMLDivElement | null>(
    null,
  );

  const cableFeedAreas = useMemo(
    () =>
      cableTakeoff.editableTakeoff.areaFeeds.map((area) => ({
        id: area.id,
        label:
          cableTakeoff.editableTakeoff.sourceNames[area.sourceLabel] ??
          area.sourceLabel,
        points: area.polygon,
        isFocused: karlBoardUnderReview
          ? area.sourceLabel === karlBoardUnderReview
          : undefined,
      })),
    [
      cableTakeoff.editableTakeoff.areaFeeds,
      cableTakeoff.editableTakeoff.sourceNames,
      karlBoardUnderReview,
    ],
  );

  const canFlashRipple = rightTab === "plan" && totalElementCount > 0;

  const handleFlashRipple = useCallback(() => {
    floorPlanPanelHandlers.current?.flashRipple?.();
  }, []);

  const handlePlanChange = useCallback(
    (index: number) => {
      resetInteraction();
      selectPlanRecord(index);
      setElementSearch("");
      setCableSearch("");
      setViewerMode("image");
    },
    [resetInteraction, selectPlanRecord, setElementSearch, setCableSearch],
  );

  const handleRightTabChange = useCallback(
    (nextTab: RightTab) => {
      resetInteraction();
      // Every tab click resets the plan selection, so the room goes with it
      clearRoomSelection();
      setRightTab(nextTab);
      if (nextTab === "plan") {
        if (selectedPlan) resetPlanSelection(selectedPlan);
        floorPlanPanelHandlers.current?.zoomToFit?.();
        return;
      }
      if (nextTab === "cables") {
        handleSelectAllRoutes();
        return;
      }
      if (nextTab === "board") {
        selectFirstItemType();
      }
    },
    [
      resetInteraction,
      clearRoomSelection,
      selectedPlan,
      resetPlanSelection,
      handleSelectAllRoutes,
      selectFirstItemType,
    ],
  );

  const handleRightTabClick = useCallback(
    (nextTab: RightTab) => {
      if (nextTab !== rightTab && nextTab !== "board") {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_TAB_CHANGED, {
          tab: ANALYTICS_TAB_BY_RIGHT_TAB[nextTab],
        });
      }
      handleRightTabChange(nextTab);
    },
    [rightTab, handleRightTabChange],
  );

  const handleWorkModeChange = useCallback(
    (nextMode: WorkMode) => {
      if (nextMode !== workMode) {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_WORK_MODE_CHANGED, {
          workMode: nextMode,
        });
      }
      clearRoomSelection();
      setWorkMode(nextMode);
      handleRightTabChange("plan");
    },
    [workMode, clearRoomSelection, handleRightTabChange],
  );

  const currentLayoutView = floorPlanLayoutView.state.view;
  const setLayoutView = floorPlanLayoutView.setView;

  const handleLayoutViewChange = useCallback(
    (nextView: FloorPlanLayoutView) => {
      if (nextView !== currentLayoutView) {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_LAYOUT_VIEW_CHANGED, {
          view: nextView,
        });
      }
      setLayoutView(nextView);
    },
    [currentLayoutView, setLayoutView],
  );

  // Execution keeps the right panel on "plan"; its tab row switches the layout view instead.
  const pickRightTab = useCallback(
    (preferred: RightTab) =>
      isExecutionMode ? "plan" : pickBestTab(selectedPlan, false, preferred),
    [isExecutionMode, selectedPlan],
  );

  const handleSidebarModeChange = useCallback(
    (mode: SidebarMode) => {
      resetInteraction();
      setSidebarMode(mode);
      setViewerMode("image");
      if (mode === "plans") {
        const effectiveTab = pickRightTab(rightTab);
        if (effectiveTab !== rightTab) setRightTab(effectiveTab);
      } else if (mode === "diagrams") {
        setRightTab("board");
        selectFirstItemType();
      }
    },
    [resetInteraction, pickRightTab, rightTab, selectFirstItemType],
  );

  const handleDiagramChange = useCallback(
    (index: number) => {
      resetInteraction();
      selectDiagramRecord(index);
      setViewerMode("image");
    },
    [resetInteraction, selectDiagramRecord],
  );

  const handlePlanRenamed = useCallback(
    (recordIndex: number, newName: string) => {
      const record = planRecords[recordIndex];
      if (!record) return;
      renamePlan({ floorPlanId: record.id, newName });
      updatePlanRecordName(recordIndex, newName);
    },
    [planRecords, renamePlan, updatePlanRecordName],
  );

  const selectedPlanImage = useMemo(
    () => selectedPlan?.planImage ?? "",
    [selectedPlan?.planImage],
  );

  const handleElementGroupClick = useCallback(
    (groupLabel: string) => {
      const groupTypeId =
        selectedPlan?.groupMeta?.[groupLabel]?.element_type_id;
      if (groupTypeId !== undefined) {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_SELECTED, {
          groupKind: "elements",
          groupTypeId: String(groupTypeId),
          elementCount: (byGroupId[groupLabel] ?? []).length,
        });
      }
      handleSelectGroup(groupLabel);
    },
    [selectedPlan, byGroupId, handleSelectGroup],
  );

  const trackElementSelected = useCallback(
    (elementId: string) => {
      if (elementId === selectedElement?.id) return;
      const groupLabel = findElementGroupLabel({
        groupIds,
        byGroupId,
        elementId,
      });
      const elementTypeId = groupLabel
        ? selectedPlan?.groupMeta?.[groupLabel]?.element_type_id
        : undefined;
      if (elementTypeId === undefined) return;
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_ELEMENT_SELECTED, {
        elementTypeId: String(elementTypeId),
      });
    },
    [selectedElement, groupIds, byGroupId, selectedPlan],
  );

  const selectGroupElement = useCallback(
    (element: { id: string }) => {
      trackElementSelected(element.id);
      handleSelectElement(element.id);
    },
    [trackElementSelected, handleSelectElement],
  );

  const handleRouteGroupClick = useCallback(
    (groupLabel: string) => {
      const routeGroup = byGroupLabel[groupLabel];
      if (routeGroup) {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_SELECTED, {
          groupKind: "routes",
          groupTypeId: String(routeGroup.route_type_id),
          elementCount: routeGroup.routes.length,
        });
      }
      handleSelectRouteGroup(groupLabel);
    },
    [byGroupLabel, handleSelectRouteGroup],
  );

  const handleMapSelectObject = useCallback(
    (objectId: string) => {
      if (isCablesView) {
        selectPlanRoute(objectId);
        cablesTabHandlers.current?.scrollToSelectedSection();
      } else {
        trackElementSelected(objectId);
        selectPlanElement(objectId);
        elementsTabHandlers.current?.scrollToSelectedSection();
      }
    },
    [isCablesView, selectPlanRoute, trackElementSelected, selectPlanElement],
  );

  const handleOpenGridReview = useCallback(() => {
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GRID_REVIEW_OPENED);
    openGridReview();
  }, [openGridReview]);

  const selectedRouteGroup = useMemo(
    () =>
      routeGroups.find((group) => group.label === selectedRouteGroupLabel) ??
      null,
    [routeGroups, selectedRouteGroupLabel],
  );

  const circuitPathsState = useMemo(() => {
    if (!isCablesView) return null;

    const baseGroups = isAllRoutesMode
      ? routeGroups
      : selectedRouteGroup
        ? [selectedRouteGroup]
        : null;

    if (!baseGroups || baseGroups.length === 0) return null;

    const omitRouteId =
      isPathEditing && selectedRoute ? selectedRoute.id : null;

    const groups = omitRouteId
      ? baseGroups.map((group) => ({
          ...group,
          routes: group.routes.filter((route) => route.id !== omitRouteId),
        }))
      : baseGroups;

    return buildGroupCircuitPathsState(groups);
  }, [
    isCablesView,
    isAllRoutesMode,
    routeGroups,
    selectedRouteGroup,
    isPathEditing,
    selectedRoute,
  ]);

  const visibleCircuitPathsState = useMemo(() => {
    if (!circuitPathsState || hiddenRouteIds.size === 0)
      return circuitPathsState;
    return {
      ...circuitPathsState,
      paths: circuitPathsState.paths.filter(
        (path) => !hiddenRouteIds.has(path.routeId),
      ),
    };
  }, [circuitPathsState, hiddenRouteIds]);

  const visibleSelectedGroupElements = useMemo(() => {
    if (hiddenElementIds.size === 0) return selectedGroupElements;
    return selectedGroupElements.filter(
      (element) => !hiddenElementIds.has(element.id),
    );
  }, [selectedGroupElements, hiddenElementIds]);

  const selectedRouteObject = useMemo(() => {
    if (!isTraversing || !selectedRoute) return undefined;
    const x = selectedRoute.nodes?.[0]?.x ?? 0;
    const y = selectedRoute.nodes?.[0]?.y ?? 0;
    return {
      id: selectedRoute.id,
      rgb:
        selectedRouteGroup?.rgb ?? ([255, 255, 0] as [number, number, number]),
      bbox: { x1: x, y1: y, x2: x, y2: y },
      status: "approved",
    };
  }, [isTraversing, selectedRoute, selectedRouteGroup]);

  const zoomTargetPaths = useMemo(() => {
    if (!isCablesView || !circuitPathsState) return EMPTY_ARRAY;
    if (!isTraversing || !selectedRoute) return circuitPathsState.paths;
    return circuitPathsState.paths.filter(
      (path) => path.routeId === selectedRoute.id,
    );
  }, [isCablesView, circuitPathsState, isTraversing, selectedRoute]);

  useAutoZoomOnCableView({
    isDistributionBoardCablesView: isCablesView,
    circuitPaths: zoomTargetPaths,
    distributionBoardPosition:
      circuitPathsState?.distributionBoardPosition ?? null,
    floorPlanPanelHandlers,
  });

  const { handleExportPdf, exportElementsByGroupId, exportRouteGroups } =
    useExportFloorPlanPdf({
      selectedPlan,
      isCablesView,
      isExecutionMode,
      routeGroups,
      byGroupId: roomScopedPlanElements.byGroupId,
      rooms: roomsEditor.rooms,
    });

  const { tourProps: onboardingTourProps, startTour: startOnboardingTour } =
    useReviewPageOnboarding({
      canEdit,
      canDelete,
      // The tour's anchors are not on screen while the rooms list is shown
      groupIds: roomSelection.isRoomsLevel ? EMPTY_ARRAY : groupIds,
      selectedGroupLabel,
      hasDiagrams: diagramRecords.length > 0,
      interactionIdle: interactionMode.kind === "idle",
      isTraversing,
      setRightTab,
      onSelectGroup: handleSelectGroup,
      onStopTraversing: stopTraversing,
    });

  const applyTrayVerdictHistoryChanges =
    cableTakeoff.trayVerdicts.applyHistoryChanges;

  const handleReverted = useCallback(
    ({ subject, changes }: HistoryUndoRevertedArgs) => {
      if (subject !== null) applyHistoryChanges({ subject, changes });
      applyApprovalChanges({ changes });
      if (subject?.kind === "plan") {
        followElementHistoryChanges({
          floorPlanId: subject.floorPlanId,
          changes,
        });
        followRouteHistoryChanges({
          floorPlanId: subject.floorPlanId,
          changes,
        });
        // The cable tab holds its verdicts outside the plan cache, so they are patched on their own.
        applyTrayVerdictHistoryChanges({
          floorPlanId: subject.floorPlanId,
          changes,
        });
      }
    },
    [
      applyHistoryChanges,
      applyApprovalChanges,
      followElementHistoryChanges,
      followRouteHistoryChanges,
      applyTrayVerdictHistoryChanges,
    ],
  );

  const historyUndo = useHistoryUndo({
    projectId: Number(projectId),
    isDiagramsMode,
    selectedFloorPlanId: selectedFloorPlanId ?? null,
    selectedDiagramId: selectedDiagram?.diagram.id ?? null,
    onReverted: handleReverted,
    isOwnUndoAvailable:
      canEdit && (isDiagramsMode || rightTab !== "cabletakeoff"),
    // Overview is paused from the click on, before its lazy modal registers; the tour is not a modal.
    isOwnUndoPaused:
      interactionMode.kind !== "idle" ||
      isGridReviewOpen ||
      isOverviewOpen ||
      onboardingTourProps.run,
  });

  useEffect(() => {
    if (selectedFloorPlanId === prevFloorPlanIdRef.current) return;
    prevFloorPlanIdRef.current = selectedFloorPlanId;
    if (selectedPlan && !isDiagramsMode) {
      setRightTab((prev) => pickRightTab(prev));
    }
  }, [selectedFloorPlanId, selectedPlan, isDiagramsMode, pickRightTab]);

  return (
    <div className="h-screen w-screen flex flex-col bg-gray-100 overflow-hidden">
      <ReviewPageHeaderV3
        onBack={onBack}
        projectName={projectName}
        showResearchReportButton={hasResearchReport}
        onOpenOverview={openOverviewModal}
        exportButton={
          <ExportExcelButtonV3
            size="md"
            align="right"
            isDisabled={isLoadingApprovals}
            projectId={projectId}
            projectName={projectName}
            canManagePrices={canManagePrices}
            cables={cableTakeoff.cablesForHeader}
            planRecords={planRecords}
            boqExtraction={boqExtraction ?? null}
            isFloorPlanGroupApproved={isFloorPlanGroupApproved}
            isCableGroupApproved={isCableGroupApproved}
            isQuantityGroupApproved={isQuantityGroupApproved}
          />
        }
        onViewResearchReport={onViewResearchReport}
        onOpenAdditionalUpload={canEdit ? openAdditionalUploadModal : undefined}
        onViewUploadedFiles={
          unfinishedUploadFiles.length > 0 ? openUploadedFilesModal : undefined
        }
        uploadedFilesCount={unfinishedUploadFiles.length}
        onStartTour={startOnboardingTour}
        onToggleHistory={isCableTakeoffView ? undefined : toggleHistoryDrawer}
        undoRedo={historyUndo.ownUndoRedo}
      />
      <AdditionalUploadModal
        isOpen={isAdditionalUploadOpen}
        onClose={closeAdditionalUploadModal}
        projectId={Number(projectId)}
        onUploadComplete={handleAdditionalUploadComplete}
      />
      <UploadedFilesModal
        isOpen={isUploadedFilesModalOpen}
        onClose={closeUploadedFilesModal}
        files={unfinishedUploadFiles}
      />
      <ExportSelectionModal
        isOpen={isExportModalOpen}
        onClose={closeExportModal}
        onExport={handleExportPdf}
        isCablesView={isCablesView}
        isAllElementsMode={isAllElementsMode}
        isAllRoutesMode={isAllRoutesMode}
        groupIds={roomScopedPlanElements.groupIds}
        byGroupId={exportElementsByGroupId}
        selectedGroupLabel={selectedGroupLabel}
        iconByGroupId={iconByGroupId}
        routeGroups={exportRouteGroups}
        selectedRouteGroupLabel={selectedRouteGroup?.label ?? null}
      />
      {isOverviewOpen && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
          }
        >
          <OverviewModal
            isOpen={isOverviewOpen}
            onClose={closeOverviewModal}
            projectId={projectId}
            projectName={projectName}
            boqExtraction={boqExtraction ?? null}
            planRecords={planRecords}
            cables={cableTakeoff.cablesForHeader}
            isFloorPlanGroupApproved={isFloorPlanGroupApproved}
            isCableGroupApproved={isCableGroupApproved}
            isQuantityGroupApproved={isQuantityGroupApproved}
            canEdit={canEdit}
            canManagePrices={canManagePrices}
            onRenameGroups={renameGroupsFromOverview}
            onRenameRouteGroups={renameRouteGroupsFromOverview}
            onSetGroupsLvMatch={setFloorPlanGroupsLvMatch}
          />
        </Suspense>
      )}
      <ConfirmActionModal {...modalProps} />
      <ConfirmActionModal {...historyUndo.revertConfirmProps} />
      <GridReviewModal
        isOpen={isGridReviewOpen}
        onClose={closeGridReview}
        scopeTitle={
          isAllElementsMode
            ? t("smartSurveyor.reviewPage.allElements")
            : (selectedGroupLabel ?? "")
        }
        isAllElementsMode={isAllElementsMode}
        currentGroupLabel={
          isAllElementsMode ? null : (selectedGroupLabel ?? null)
        }
        isSelectable={canEdit && selectedPlan !== null}
        plan={selectedPlan}
        elements={selectedGroupElements}
        onBulkDeleteElements={bulkDeleteFloorPlanElements}
        onBulkMoveElements={bulkMoveFloorPlanElements}
        onJumpToElement={handleGridReviewJump}
        onScopeEmptied={handleGridReviewScopeEmptied}
      />
      {renameModalProps && <ApplyRenameToProjectModal {...renameModalProps} />}
      <ChangePageScaleModal
        isOpen={pageScaleRecalibration.isConfirmOpen}
        onClose={pageScaleRecalibration.closeConfirm}
        onPickPoints={pageScaleRecalibration.confirmRecalibrate}
        onResetToDetected={pageScaleRecalibration.resetToDetected}
        canResetToDetected={pageScaleRecalibration.canResetToDetected}
      />
      <OnboardingTour {...onboardingTourProps} />
      <PageCalibrationModal
        isOpen={calibrationState?.state.phase === "modal"}
        pdfDistance={calibration.pdfDistance}
        isSubmitting={calibration.isSubmitting}
        onSubmit={calibration.submit}
        onCancel={stopCalibrating}
      />
      <div className="flex-1 flex overflow-hidden relative">
        <SmartSurveyorSidebar
          isCollapsed={isPlanRailCollapsed}
          onCollapsedChange={setIsPlanRailCollapsed}
          sidebarMode={sidebarMode}
          onSidebarModeChange={handleSidebarModeChange}
          isTraversing={isTraversing}
          planRecords={planRecords}
          selectedPlanRecordIndex={selectedPlanRecordIndex}
          pendingPlanRecordIndex={pendingPlanRecordIndex}
          onSelectPlan={handlePlanChange}
          onClearPlanSelection={clearPlanSelection}
          diagramRecords={diagramRecords}
          selectedDiagramRecordIndex={selectedDiagramRecordIndex}
          pendingDiagramRecordIndex={pendingDiagramRecordIndex}
          onSelectDiagram={handleDiagramChange}
          onPlanRenamed={canEdit ? handlePlanRenamed : undefined}
          onPlanArchivedAtChanged={
            canEdit ? updatePlanRecordArchivedAt : undefined
          }
          onPlanDeleted={canDelete ? removePlanRecord : undefined}
          folderRecords={folderRecords}
          onPlanFolderChanged={canEdit ? updatePlanRecordFolderId : undefined}
          onFoldersCreated={canEdit ? upsertFolderRecords : undefined}
          onFolderRenamed={canEdit ? renameFolderRecord : undefined}
          onFolderDeleted={canEdit ? removeFolderRecord : undefined}
          projectId={projectId}
          onFoldersImported={canEdit ? applyFolderImport : undefined}
        />
        <div className="flex-1 p-4 overflow-hidden">
          <div
            className={clsx(
              "grid gap-2 h-full",
              isCableTakeoffView
                ? "grid-cols-[minmax(0,1fr)_380px_520px]"
                : isHistoryShown
                  ? "grid-cols-[minmax(0,1fr)_500px_400px]"
                  : "grid-cols-[minmax(0,1fr)_500px]",
            )}
          >
            <div className="flex flex-col gap-1 h-full min-h-0 overflow-hidden">
              <div
                data-onboarding={ONBOARDING_ANCHORS.viewer}
                className={clsx(
                  "min-h-0 w-full h-full rounded-lg",
                  isTraversing
                    ? "border-2 border-blue-400"
                    : "border-2 border-transparent",
                )}
              >
                {isDiagramsMode ? (
                  <DiagramViewerV3
                    diagramName={selectedDiagram?.diagram.name ?? ""}
                    selectedOccurrence={selectedOccurrence}
                    canGoPrev={canGoPrevOccurrence}
                    canGoNext={canGoNextOccurrence}
                    goPrev={goPrevOccurrence}
                    goNext={goNextOccurrence}
                    goToIndex={handleSelectOccurrence}
                    selectedIndex={selectedOccurrenceIndex}
                    total={occurrenceCount}
                    viewerMode={viewerMode}
                    onViewerModeChange={setViewerMode}
                  />
                ) : selectedPlan === null && pendingPlanRecordIndex === null ? (
                  <div className="h-full flex">
                    <EmptyStateMessage
                      message={t(
                        "smartSurveyor.reviewPage.noFloorPlanSelected",
                      )}
                    />
                  </div>
                ) : (
                  <FloorPlanPanelWrapper
                    key={getPreviewUrlKey(selectedPlanImage)}
                    ref={floorPlanPanelHandlers}
                    areNotesHidden={!areNotesVisible || isExecutionMode}
                    plan={{
                      previewUrl: selectedPlanImage ?? "",
                      pdfUrl: selectedPlan?.pdfUrl ?? "",
                      pageNumber: 1,
                      name:
                        selectedPlan?.label ??
                        t("smartSurveyor.common.floorPlan"),
                      imageWidthPx: selectedPlan?.imageWidthPx ?? 0,
                      imageHeightPx: selectedPlan?.imageHeightPx ?? 0,
                      isArchived:
                        selectedPlanRecordIndex !== null &&
                        planRecords[selectedPlanRecordIndex].archivedAt !==
                          null,
                    }}
                    viewerMode={viewerMode}
                    onViewerModeChange={setViewerMode}
                    imageScaleFactor={imageScaleFactor}
                    rotation={pageRotation}
                    onRotate={canEdit ? rotateClockwise : undefined}
                    zoomToFitOnImageLoad={isCablesView ? "never" : "always"}
                    showObjectNavigation={isTraversing}
                    navigationObjects={
                      isCablesView
                        ? selectedGroupRoutes
                        : isCreationMode
                          ? undefined
                          : selectedGroupElements
                    }
                    onAfterNavigate={
                      isCablesView ? skipPanelNavigationZoom : undefined
                    }
                    isAutoZoomEnabled={false}
                    exportActions={planExportActions}
                    renderToolbar={({ isHoveringPlan }) => (
                      <PlanInteractionHint
                        isHoveringPlan={isHoveringPlan}
                        interactionMode={interactionMode}
                        isRouteDrawingActive={isRouteDrawingActive}
                        isPolygonEditingActive={
                          selectionAreas.isAreaEditingActive ||
                          roomsEditor.isRoomEditingActive
                        }
                        creationModifier={creationModifier}
                      />
                    )}
                    mode={
                      mapInteraction.isPickZoneActive ||
                      doesTakeoffOwnPlanClicks ||
                      roomSelection.isRoomsLevel
                        ? "pick_zone"
                        : "default"
                    }
                    // Handles ONLY while a shape is open for editing. They are
                    // the grab targets, so drawing them on a merely-selected run
                    // told him he could drag a cable he had only clicked.
                    nodeHandlesForRouteId={
                      isCableTakeoffView
                        ? cableTakeoff.cableDirectEdit.shapeEditCircuit
                        : null
                    }
                    hideViewerModeToggle={
                      interactionMode.kind !== "idle" ||
                      doesTakeoffOwnPlanClicks
                    }
                    // Sources and route nodes are both grabbable here: route
                    // editing is live again now that an edit is keyed on the
                    // path's own destinationId rather than its slot.
                    onAreaSelect={
                      doesTakeoffOwnPlanClicks && cableTakeoff.assignMode.isOn
                        ? cableTakeoff.assignMode.selectArea
                        : undefined
                    }
                    onMapClick={
                      doesTakeoffOwnPlanClicks && cableTakeoff.assignMode.isOn
                        ? cableTakeoff.assignMode.toggleAt
                        : doesTakeoffOwnPlanClicks
                          ? cableTakeoff.cableDirectEdit.onMapClick
                          : mapInteraction.isPickZoneActive
                            ? mapInteraction.onMapClick
                            : roomSelection.isRoomsLevel
                              ? handleLayoutPlanClick
                              : undefined
                    }
                    onEmptyMapClick={
                      roomSelection.selectedRoom && !interaction.isEditingRoom
                        ? roomSelection.handleOutsideClick
                        : undefined
                    }
                    onSelectedBboxChange={
                      canEdit && (isEditingElement || isCreationMode)
                        ? handleChangeSelectedBbox
                        : undefined
                    }
                    onMapMouseMove={
                      doesTakeoffOwnPlanClicks
                        ? cableTakeoff.cableDirectEdit.onMapMouseMove
                        : mapInteraction.onMapMouseMove
                    }
                    routeCreationOverlay={mapInteraction.overlay}
                    // On the rooms level the detected layout is the picture; a room
                    // entered or edited is drawn alone on top of it, as before.
                    polygonsOverlay={
                      isExecutionMode
                        ? (roomsEditor.overlay ??
                          (roomSelection.isRoomsLevel
                            ? null
                            : roomSelection.overlay))
                        : selectionAreas.selectionAreasOverlay
                    }
                    floorPlanLayoutOverlay={
                      isLayoutShown
                        ? floorPlanLayoutView.overlay
                        : isCableTakeoffView
                          ? cableRooms.overlay
                          : null
                    }
                    setSelectedObject={handleMapSelectObject}
                    detectedObjects={
                      isCreationMode
                        ? creationPreviewElements
                        : isCablesView || isCableTakeoffView
                          ? EMPTY_ARRAY
                          : visibleSelectedGroupElements
                    }
                    backgroundObjects={
                      isCreationMode ? visibleSelectedGroupElements : undefined
                    }
                    selectedObject={
                      isCablesView
                        ? selectedRouteObject
                        : isCreationMode
                          ? creationActivePreviewElement
                          : isTraversing
                            ? draftSelectedElement
                            : undefined
                    }
                    circuitPaths={
                      isCablesView
                        ? visibleCircuitPathsState
                        : isCableTakeoffView
                          ? cableTakeoff.cableTakeoffPaths
                          : null
                    }
                    circuitStrokeScale={isCableTakeoffView ? 0.4 : undefined}
                    areRouteLinesHidden={
                      isCableTakeoffView
                        ? cableTakeoff.areRouteLinesHidden
                        : undefined
                    }
                    cableTrays={
                      isCableTakeoffView && cableTakeoff.areTraysShown
                        ? cableTakeoff.cableTrays
                        : undefined
                    }
                    highlightTrayType={cableTakeoff.highlightTrayType}
                    focusedTrayType={
                      isCableTakeoffView ? cableTakeoff.focusedTrayType : null
                    }
                    circuitBoardMarkers={
                      isCableTakeoffView
                        ? cableTakeoff.cableSourceMarkers
                        : undefined
                    }
                    removedMarkers={
                      isCableTakeoffView
                        ? cableTakeoff.editableTakeoff.removedDeviceMarkers
                        : undefined
                    }
                    candidateDeviceMarkers={
                      isCableTakeoffView
                        ? cableTakeoff.candidateDeviceMarkers
                        : undefined
                    }
                    unroutedDeviceMarkers={
                      isCableTakeoffView
                        ? cableTakeoff.unroutedDeviceMarkers
                        : undefined
                    }
                    selectedDeviceMarkers={
                      isCableTakeoffView
                        ? cableTakeoff.selectedDeviceMarkers
                        : undefined
                    }
                    assignDeviceBoxes={
                      isCableTakeoffView
                        ? cableTakeoff.assignDeviceBoxes
                        : undefined
                    }
                    selectedDeviceMarkersBlink={
                      isCableTakeoffView && cableTakeoff.isDeviceSpotlight
                    }
                    reshapeView={
                      isCableTakeoffView
                        ? cableTakeoff.cableDirectEdit.reshapeView
                        : null
                    }
                    highlightMarker={
                      isCableTakeoffView ? cableTakeoff.highlightMarker : null
                    }
                    heightMarks={
                      isCableTakeoffView ? cableTakeoff.heightMarks : undefined
                    }
                    zoneDraft={
                      isCableTakeoffView
                        ? cableTakeoff.cableDirectEdit.zoneDraft
                        : null
                    }
                    // A rule he cannot see is a rule he cannot account for, and
                    // this one re-points runs he never named.
                    feedAreas={isCableTakeoffView ? cableFeedAreas : undefined}
                    // His own toggle: a plan traced wall by wall would otherwise
                    // bury the cable it exists to route.
                    planWalls={
                      isCableTakeoffView && cableTakeoff.planWalls.areWallsShown
                        ? cableTakeoff.planWalls.walls
                        : undefined
                    }
                    wallDraft={
                      isCableTakeoffView
                        ? cableTakeoff.cableDirectEdit.wallDraft
                        : null
                    }
                    // Only while he corrects them, and not mid-stroke: a click then is a corner, not a pick.
                    wallSelection={
                      isCableTakeoffView &&
                      cableTakeoff.planWalls.isCorrecting &&
                      !cableTakeoff.cableDirectEdit.wallDraft
                        ? {
                            selectedWallId:
                              cableTakeoff.planWalls.selectedWallId,
                            onSelect: cableTakeoff.planWalls.selectWall,
                          }
                        : null
                    }
                    // Crosshair only while something is actually being placed;
                    // the tab sits in pick_zone the whole time it is open.
                    pickZoneCursor={
                      (isRoomSelectionEnabled && roomSelection.isRoomsLevel) ||
                      (doesTakeoffOwnPlanClicks &&
                        !cableTakeoff.cableDirectEdit.grabbed &&
                        !cableTakeoff.cableDirectEdit.zoneDraft &&
                        // Drawing a wall is placing something, so the pointer says
                        // so: a grab hand over a plan he is drawing on is the
                        // cursor telling him the opposite of what is happening.
                        !cableTakeoff.cableDirectEdit.wallDraft)
                        ? "grab"
                        : "crosshair"
                    }
                  />
                )}
              </div>
            </div>
            <div className="w-full h-full bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col overflow-hidden p-4 !pt-3">
              <TabsRoot
                value={rightTab}
                onValueChange={handleRightTabClick}
                className="h-full flex flex-col min-h-0"
                styles={{ variant: "underline" }}
              >
                {!isDiagramsMode && (
                  <div className="mb-2">
                    <WorkModeTabs
                      value={workMode}
                      onChange={handleWorkModeChange}
                    />
                  </div>
                )}
                <div data-onboarding={ONBOARDING_ANCHORS.rightTabs}>
                  {/* Execution keeps rightTab on "plan"; its tab row switches the layout view instead. */}
                  {isExecutionMode && !isDiagramsMode ? (
                    !isLayoutLoading && (
                      <>
                        <LayoutViewTabs
                          value={floorPlanLayoutView.state.view}
                          availableViews={
                            floorPlanLayoutView.state.availableViews
                          }
                          onChange={handleLayoutViewChange}
                        />
                        <LayoutDetectionStrip
                          layoutDetection={layoutDetection}
                          canEdit={canEdit && !isEditingRooms}
                        />
                      </>
                    )
                  ) : (
                    <TabsList className="[&>div]:gap-0">
                      {isDiagramsMode ? (
                        <TabsTrigger value="board" className="flex-1">
                          {t("smartSurveyor.reviewPage.tabs.quantityGroups")}
                        </TabsTrigger>
                      ) : (
                        <>
                          <TabsTrigger value="plan" className="flex-1">
                            {t(
                              "smartSurveyor.reviewPage.tabs.floorPlanElements",
                            )}
                          </TabsTrigger>
                          <TabsTrigger value="cables" className="flex-1">
                            {t("smartSurveyor.reviewPage.tabs.cableGroups")}
                          </TabsTrigger>
                          {canSeeCableTakeoff && (
                            <TabsTrigger
                              value="cabletakeoff"
                              className="flex-1"
                            >
                              <span className="inline-flex items-center gap-1.5">
                                <span className="flex-1 shrink-0 whitespace-nowrap">
                                  {t("smartSurveyor.cablesBeta.tabLabelShort")}
                                </span>
                                <BetaBadge />
                              </span>
                            </TabsTrigger>
                          )}
                        </>
                      )}
                    </TabsList>
                  )}
                </div>
                <TabsPanels className="mt-4 overflow-hidden flex-1 min-h-0">
                  <TabsPanel
                    value="board"
                    className="w-full h-full flex flex-col min-h-0"
                  >
                    <QuantityGroupsTabV3
                      canEdit={canEdit}
                      boardSearch={boardSearch}
                      onBoardSearchChange={setBoardSearch}
                      sort={boardSort}
                      onSortChange={setBoardSort}
                      selectedDiagram={selectedDiagram}
                      selectedItemType={selectedItemType}
                      selectedOccurrenceIndex={selectedOccurrenceIndex}
                      selectedDiagramItemTypeIds={selectedDiagramItemTypeIds}
                      selectedDiagramRecordIndex={selectedDiagramRecordIndex}
                      allDiagramItemsApproved={allDiagramItemsApproved}
                      isLoadingApprovals={isLoadingApprovals}
                      isQuantityGroupApproved={isQuantityGroupApproved}
                      onOpenBulkApproval={openForQuantityGroups}
                      onToggleQuantityGroupApproval={
                        toggleQuantityGroupApproval
                      }
                      onSelectItemType={handleSelectItemType}
                      onSelectOccurrence={handleSelectOccurrence}
                      isTraversing={isTraversing}
                      onStartTraversing={handleStartBoardTraversing}
                      onStopTraversing={stopTraversing}
                      seenIds={boardSeenIds}
                      onGoNextUnseen={goNextUnseenOccurrence}
                      onPrevOccurrence={goPrevOccurrence}
                      onNextOccurrence={goNextOccurrence}
                      boqPositions={boqPositions}
                      onSaveLvMatch={handleSaveBoardItemLvMatch}
                      onDeleteItemGroup={handleDeleteItemGroup}
                    />
                  </TabsPanel>
                  <TabsPanel
                    value="plan"
                    className="w-full h-full flex flex-col min-h-0"
                  >
                    <FloorPlanElementsTabV3
                      ref={elementsTabHandlers}
                      canEdit={canEdit && selectedPlan !== null}
                      form={selectedElementForm}
                      elementSearch={elementSearch}
                      onElementSearchChange={setElementSearch}
                      sort={elementSort}
                      onSortChange={setElementSort}
                      groupIds={groupIds}
                      byGroupId={byGroupId}
                      exportElementsByGroupId={exportElementsByGroupId}
                      totalElementCount={totalElementCount}
                      groupMeta={selectedPlan?.groupMeta}
                      selectedGroupLabel={selectedGroupLabel}
                      selectedGroupElements={selectedGroupElements}
                      selectedElement={selectedElement}
                      selectedPlanRecordIndex={selectedPlanRecordIndex}
                      allPlanElementsApproved={allPlanElementsApproved}
                      isLoadingApprovals={isLoadingApprovals}
                      isFloorPlanGroupApproved={isFloorPlanGroupApproved}
                      onOpenBulkApproval={openForElementTypes}
                      onToggleFloorPlanGroupApproval={
                        toggleFloorPlanGroupApproval
                      }
                      onSelectGroup={handleElementGroupClick}
                      onJumpToSelectedElementGroup={
                        handleJumpToSelectedElementGroup
                      }
                      onSelectElement={selectGroupElement}
                      isEditingElement={isEditingElement}
                      onToggleEditMode={toggleEditMode}
                      onDeleteElement={handleDeleteElement}
                      onDeleteGroup={handleDeleteGroup}
                      onRenameGroup={handleRenameGroup}
                      onMarkElementGroupReviewed={
                        handleMarkElementGroupReviewed
                      }
                      onToggleSuspicious={
                        isSuperAdmin ? handleSetNameStatus : undefined
                      }
                      isCreationMode={isCreationMode}
                      onOpenCreationMode={openCreationMode}
                      workMode={workMode}
                      isSelectionAreasMode={selectionAreas.isSelectionAreasMode}
                      selectionAreasState={selectionAreas.state}
                      onOpenAreasPanel={selectionAreas.openAreasPanel}
                      areasCount={selectionAreas.areasCount}
                      areAreasVisible={areAreasVisible}
                      onToggleAreasVisibility={toggleAreasVisibility}
                      notesCount={notesCount}
                      areNotesVisible={areNotesVisible}
                      onToggleNotesVisibility={toggleNotesVisibility}
                      roomsCreation={roomsEditor.creation}
                      roomInspector={roomsEditor.inspector}
                      onOpenRoomsPanel={roomsEditor.startCreating}
                      isLayoutDetectionRunning={layoutDetection.isRunning}
                      layoutViewState={floorPlanLayoutView.state}
                      isLayoutLoading={isLayoutLoading}
                      isLayoutError={isLayoutError}
                      roomElementCounts={
                        roomScopedPlanElements.roomElementCounts
                      }
                      selectedRoom={roomSelection.selectedRoom}
                      onSelectRoom={roomSelection.selectRoom}
                      onClearRoomSelection={roomSelection.clearRoomSelection}
                      onEditRoom={roomsEditor.startEditing}
                      roomsMerge={roomsEditor.merge}
                      selectedZone={selectedZone.selectedZone}
                      selectedZoneRoomIds={selectedZone.selectedZoneRoomIds}
                      onSelectZone={selectedZone.selectZone}
                      onClearZoneSelection={selectedZone.clearZoneSelection}
                      onFlashRipple={
                        canFlashRipple ? handleFlashRipple : undefined
                      }
                      creationModeState={creationModeState}
                      isMultiAddModeEnabled={isMultiAddModeEnabled}
                      onToggleMultiAddMode={toggleMultiAdd}
                      isTraversing={isTraversing}
                      onStartTraversing={handleStartTraversing}
                      onStopTraversing={stopTraversing}
                      onOpenGridReview={handleOpenGridReview}
                      isGridReviewOpen={isGridReviewOpen}
                      seenIds={seenIds}
                      isAllElementsMode={isAllElementsMode}
                      onSelectAllElements={handleSelectAllElements}
                      hiddenElementIds={hiddenElementIds}
                      onToggleElementsVisibility={toggleElementsVisibility}
                      areAllElementsHidden={areAllElementsHidden}
                      boqPositions={boqPositions}
                      onSaveLvMatch={handleSaveElementGroupLvMatch}
                      iconByGroupId={iconByGroupId}
                    />
                  </TabsPanel>
                  <TabsPanel
                    value="cables"
                    className="w-full h-full flex flex-col min-h-0"
                  >
                    <CableGroupsTabV3
                      ref={cablesTabHandlers}
                      canEdit={canEdit && selectedPlan !== null}
                      workMode={workMode}
                      cableSearch={cableSearch}
                      onCableSearchChange={setCableSearch}
                      sort={cableSort}
                      onSortChange={setCableSort}
                      routeGroups={routeGroups}
                      exportRouteGroups={exportRouteGroups}
                      routeGroupLabels={routeGroupLabels}
                      byGroupLabel={byGroupLabel}
                      selectedRouteGroupLabel={selectedRouteGroupLabel}
                      selectedGroupRoutes={selectedGroupRoutes}
                      selectedRoute={selectedRoute}
                      totalCableMeters={totalCableMeters}
                      selectedPlanRecordIndex={selectedPlanRecordIndex}
                      allPlanCablesApproved={allPlanCablesApproved}
                      isLoadingApprovals={isLoadingApprovals}
                      isCableGroupApproved={isCableGroupApproved}
                      onOpenBulkApproval={openForCableGroups}
                      onToggleCableGroupApproval={toggleCableGroupApproval}
                      onSelectRouteGroup={handleRouteGroupClick}
                      onJumpToSelectedRouteGroup={
                        handleJumpToSelectedRouteGroup
                      }
                      onSelectRoute={handleSelectRoute}
                      routeGroupOptions={routeGroupOptions}
                      onRenameRouteGroup={handleRenameRouteGroup}
                      onMarkRouteGroupReviewed={handleMarkRouteGroupReviewed}
                      onToggleSuspicious={
                        isSuperAdmin ? handleSetCableTypeStatus : undefined
                      }
                      onDeleteRoute={handleDeleteRoute}
                      onDeleteRouteGroup={handleDeleteRouteGroup}
                      onChangeRouteType={handleChangeRouteType}
                      isTraversing={isTraversing}
                      onStartTraversing={handleStartRouteTraversing}
                      onStopTraversing={stopTraversing}
                      seenIds={routeSeenIds}
                      isAllRoutesMode={isAllRoutesMode}
                      onSelectAllRoutes={handleSelectAllRoutes}
                      isCreationMode={isRouteCreationMode}
                      onOpenCreationMode={openRouteCreationMode}
                      isSelectionAreasMode={selectionAreas.isSelectionAreasMode}
                      selectionAreasState={selectionAreas.state}
                      onOpenAreasPanel={selectionAreas.openAreasPanel}
                      areasCount={selectionAreas.areasCount}
                      areAreasVisible={areAreasVisible}
                      onToggleAreasVisibility={toggleAreasVisibility}
                      onOpenCalibration={startCalibrating}
                      isCalibrating={isPickingCalibration}
                      onCancelCalibration={stopCalibrating}
                      pageScaleValue={pageScaleRecalibration.pageScale}
                      pageScaleSource={pageScaleRecalibration.pageScaleSource}
                      isPageCalibrated={pageScaleRecalibration.isPageCalibrated}
                      isPageRecalibrating={
                        pageScaleRecalibration.isPageRecalibrating
                      }
                      isChangePageScaleDisabled={
                        pageScaleRecalibration.isChangePageScaleDisabled
                      }
                      onChangePageScale={pageScaleRecalibration.openConfirm}
                      onCancelPageRecalibration={stopCalibrating}
                      creationModeState={routeCreationModeState}
                      isPathEditing={isPathEditing}
                      isPathEditingDirty={isPathEditingDirty}
                      isPathEditingSubmitting={isPathEditingSubmitting}
                      pathEditingMeters={pathEditingMeters}
                      onStartPathEditing={startPathEditing}
                      onSubmitPathEditing={submitPathEditing}
                      onCancelPathEditing={cancelPathEditing}
                      hiddenRouteIds={hiddenRouteIds}
                      onToggleRoutesVisibility={toggleRoutesVisibility}
                      areAllRoutesHidden={areAllRoutesHidden}
                      boqPositions={boqPositions}
                      onSaveLvMatch={handleSaveRouteGroupLvMatch}
                    />
                  </TabsPanel>
                  {canSeeCableTakeoff && (
                    <TabsPanel
                      value="cabletakeoff"
                      className="w-full h-full flex flex-col min-h-0"
                    >
                      <CableTakeoffTabV3
                        takeoff={cableTakeoff.selectedPlanTakeoff}
                        roomLayout={cableRooms.roomLayout}
                        detailHost={
                          cableTakeoff.editableTakeoff.isSetupDone
                            ? cableDetailHost
                            : null
                        }
                        onRoomTint={cableRooms.tintRooms}
                        rooms={cableRooms.roomList}
                        // Karl's run wins over the list selection while he is reviewing
                        selectedCircuit={
                          routeUnderReviewOnPlan ??
                          cableTakeoff.selectedCableCircuit
                        }
                        onSelectCircuit={selectCableCircuitFromList}
                        onHoverCircuit={cableTakeoff.setHoveredCableCircuit}
                        edit={cableTakeoff.editableTakeoff}
                        onResetAll={() => {
                          cableTakeoff.editableTakeoff.resetAll();
                          resetKarlThread();
                        }}
                        grabbed={cableTakeoff.cableDirectEdit.grabbed}
                        onCancelGrab={cableTakeoff.cableDirectEdit.cancel}
                        onAddSource={
                          cableTakeoff.cableDirectEdit.startAddSource
                        }
                        onFeedArea={cableTakeoff.cableDirectEdit.startZone}
                        openBoard={listCircuitBoard}
                        onOpenBoard={setListCircuitBoard}
                        onCableTypeCircuits={setCableTypeCircuits}
                        zoneDraft={cableTakeoff.cableDirectEdit.zoneDraft}
                        onFinishZone={cableTakeoff.cableDirectEdit.finishZone}
                        onUnroutedListOpenChange={
                          cableTakeoff.setUnroutedListOpen
                        }
                        assignMode={cableTakeoff.assignMode}
                        circuitNames={cableTakeoff.circuitNames}
                        walls={{
                          walls: cableTakeoff.planWalls.walls,
                          draft: cableTakeoff.cableDirectEdit.wallDraft,
                          areWallsShown: cableTakeoff.planWalls.areWallsShown,
                          didSaveFail: cableTakeoff.planWalls.didSaveFail,
                          onToggleShown:
                            cableTakeoff.planWalls.setAreWallsShown,
                          onDraw: cableTakeoff.cableDirectEdit.startWall,
                          onFinish: cableTakeoff.cableDirectEdit.finishWall,
                          onCancel: cableTakeoff.cableDirectEdit.cancel,
                          onRemove: (wallId) =>
                            void cableTakeoff.planWalls.removeWall(wallId),
                        }}
                        detectedWallsSummary={
                          cableTakeoff.planWalls.detectedLayout.phase ===
                          "loaded"
                            ? cableTakeoff.planWalls.detectedLayout.summary
                            : undefined
                        }
                        trays={cableTakeoff.cableTrays}
                        drawnRouteTypes={cableTakeoff.drawnRouteTypes}
                        onSetCarriesCable={(routeTypeId, carriesCable) =>
                          void handleSetCarriesCable(routeTypeId, carriesCable)
                        }
                        areTraysShown={cableTakeoff.areTraysShown}
                        onToggleTrays={cableTakeoff.setTraysShown}
                        onHighlightTrayType={cableTakeoff.setHighlightTrayType}
                        destinationInfo={cableTakeoff.cableDestinationInfo}
                        destinationInfoById={
                          cableTakeoff.cableDestinationInfoById
                        }
                        onHighlightDestination={cableTakeoff.setHighlightMarker}
                        onShowHeightMarks={cableTakeoff.setHeightMarks}
                        pageScaleValue={pageScaleRecalibration.pageScale}
                        pageScaleSource={pageScaleRecalibration.pageScaleSource}
                        onChangePageScale={pageScaleRecalibration.openConfirm}
                        isChangePageScaleDisabled={
                          pageScaleRecalibration.isChangePageScaleDisabled
                        }
                        underReviewCircuit={karlRouteUnderReview}
                        onSpotlightDevices={cableTakeoff.setSpotlightDevices}
                        isPickingRun={Boolean(karlAwaitingRunPick)}
                        // Selecting the run IS the answer — the same handshake
                        // a click on the drawing makes, from the list where he
                        // can actually read the names. It marks the run too:
                        // an answer he cannot see land is one he has to guess at.
                        onPickRun={cableTakeoff.selectCableCircuit}
                        onAskKarlAbout={askKarlAboutRoute}
                        routeStatus={karlRouteStatus}
                        unrouted={cableTakeoff.unroutedSummary}
                        circuitKeys={circuitKeys}
                        circuitBreakers={circuitBreakers}
                        diagram={karlDiagramControl}
                        unfilledWays={unfilledWays}
                        areAllCablesShown={cableTakeoff.areAllCablesShown}
                        onToggleAllCables={cableTakeoff.setAllCablesShown}
                        feedPointOptions={feedPointOptions}
                        cableTypeOptions={cableTypeOptions}
                      />
                    </TabsPanel>
                  )}
                </TabsPanels>
              </TabsRoot>
            </div>
            {canSeeCableTakeoff && (
              <div
                className={clsx(
                  "h-full w-full min-h-0 flex-col gap-2",
                  isCableTakeoffView ? "flex" : "hidden",
                )}
              >
                <div className="flex min-h-[240px] w-full flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                  {/* Karl's own bar, not a caption over the messages: a tinted
                      band with its own edge, so he reads as a person occupying a
                      panel rather than a heading inside one. */}
                  <div className="flex shrink-0 items-center gap-2.5 border-b border-blue-200 bg-blue-50/70 px-3.5 py-2.5">
                    <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-blue-600 text-[15px] font-bold text-white shadow-sm">
                      K
                    </span>
                    <div className="min-w-0">
                      <div className="text-[16px] font-bold leading-tight tracking-tight text-slate-900">
                        {t("smartSurveyor.cablesBeta.chatTitle")}
                      </div>
                      <div className="truncate text-[11.5px] leading-tight text-slate-500">
                        {t("smartSurveyor.cablesBeta.chatSubtitle")}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={startNewKarlChat}
                      className="ml-auto flex shrink-0 items-center gap-1 rounded-full border border-blue-300 bg-white/70 px-3 py-1.5 text-[12px] font-semibold text-blue-600 transition-colors hover:bg-white"
                    >
                      <Plus size={13} />
                      {t("smartSurveyor.cablesBeta.chatNewThread")}
                    </button>
                  </div>
                  <CablesChatPanel
                    // Remount per plan so no thread, queue or in-flight reply carries over to the next plan.
                    key={
                      cableTakeoff.selectedPlanTakeoff?.plan_name ?? "no-plan"
                    }
                    projectId={projectId}
                    planName={cableTakeoff.selectedPlanTakeoff?.plan_name}
                    gaps={cableTakeoff.cableGaps}
                    generatedAt={cableTakeoff.cablesForHeader?.generated_at}
                    steps={cableTakeoff.karlSteps}
                    onFocusCircuit={cableTakeoff.setSelectedCableCircuit}
                    isActive={isCableTakeoffView}
                    onProvideReset={registerKarlReset}
                    onProvideNewChat={registerKarlNewChat}
                    onProvideAskAbout={registerKarlAskAbout}
                    onBoardUnderReview={setKarlBoardUnderReview}
                    plan={karlPlanControl}
                    diagram={karlDiagramControl}
                    sourceEdit={karlSourceEdit}
                    liveTakeoff={karlLiveTakeoff}
                    openingFacts={karlOpeningFacts}
                    isSetupDone={cableTakeoff.editableTakeoff.isSetupDone}
                    walls={karlWalls}
                  />
                </div>
                {/* What he opened in the list is shown here, below Karl; empty, it takes no room at all. */}
                <div
                  ref={setCableDetailHost}
                  data-testid="cable-detail-host"
                  className="max-h-[62%] flex-none overflow-y-auto rounded-lg border border-blue-300 bg-white shadow-sm empty:hidden"
                />
              </div>
            )}
            <HistoryDrawer
              projectId={Number(projectId)}
              subject={historyUndo.subject}
              isOpen={isHistoryShown}
              onClose={closeHistoryDrawer}
              revertingEntryId={historyUndo.revertingEntryId}
              onRevert={historyUndo.revert}
            />
          </div>
        </div>
      </div>
      <CableSourceToolbarOverlay cableTakeoff={cableTakeoff} />
    </div>
  );
};
